import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Save, Coins, Send } from "lucide-react";
import { api, apiErr, inr } from "@/lib/api";

export default function AdminLoyalty() {
  const [cfg, setCfg] = useState(null);
  const [nstats, setNstats] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/loyalty-config").then((r) => setCfg(r.data)); api.get("/admin/nudge-report").then((r) => setNstats(r.data)).catch(() => {}); }, []);
  if (!cfg) return <div className="text-slate-400">Loading…</div>;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const sendNudges = async () => {
    try { const { data } = await api.post("/admin/send-nudges"); toast.success(`Nudges sent to ${data.sent} of ${data.candidates} customer(s)`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        earn_per_amount: parseFloat(cfg.earn_per_amount) || 0,
        points_per_earn: parseFloat(cfg.points_per_earn) || 0,
        redeem_value_per_point: parseFloat(cfg.redeem_value_per_point) || 0,
        max_redeem_percent: parseFloat(cfg.max_redeem_percent) || 0,
        min_redeem_points: parseFloat(cfg.min_redeem_points) || 0,
        enabled: !!cfg.enabled,
      };
      const { data } = await api.put("/admin/loyalty-config", body);
      setCfg(data); toast.success("Loyalty settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const example = (() => {
    const earn = cfg.earn_per_amount > 0 ? (1000 / cfg.earn_per_amount) * cfg.points_per_earn : 0;
    return { earn: Math.round(earn * 100) / 100, worth: Math.round(earn * cfg.redeem_value_per_point * 100) / 100 };
  })();

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-2xl font-bold flex items-center gap-2"><Gift className="text-emerald-600" /> Loyalty Rewards</h1>

      {nstats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="nudge-roi">
          <RoiCard label="Nudges Sent" value={nstats.sent} />
          <RoiCard label="Converted" value={nstats.converted} />
          <RoiCard label="Conversion" value={`${nstats.conversion_rate}%`} />
          <RoiCard label="Revenue" value={inr(nstats.revenue)} />
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4" data-testid="loyalty-config-card">
        <label className="flex items-center gap-3 text-sm font-semibold">
          <input type="checkbox" data-testid="loyalty-enabled" checked={cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} className="accent-emerald-600 w-4 h-4" />
          Program enabled (customers earn & redeem points)
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <F label="Spend ₹ per point (earn)"><input data-testid="loy-earn-per" type="number" step="0.01" value={cfg.earn_per_amount} onChange={(e) => set("earn_per_amount", e.target.value)} className="inp" /></F>
          <F label="Points earned per that spend"><input data-testid="loy-points-per" type="number" step="0.01" value={cfg.points_per_earn} onChange={(e) => set("points_per_earn", e.target.value)} className="inp" /></F>
          <F label="₹ value of 1 point (redeem)"><input data-testid="loy-redeem-val" type="number" step="0.01" value={cfg.redeem_value_per_point} onChange={(e) => set("redeem_value_per_point", e.target.value)} className="inp" /></F>
          <F label="Max redeem — % of order total"><input data-testid="loy-max-pct" type="number" step="1" value={cfg.max_redeem_percent} onChange={(e) => set("max_redeem_percent", e.target.value)} className="inp" /></F>
          <F label="Min points needed to redeem"><input data-testid="loy-min-pts" type="number" step="0.01" value={cfg.min_redeem_points} onChange={(e) => set("min_redeem_points", e.target.value)} className="inp" /></F>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4 text-sm text-emerald-800 flex items-start gap-2">
          <Coins size={16} className="mt-0.5 shrink-0" />
          <span data-testid="loyalty-example">On a <b>{inr(1000)}</b> order a customer earns <b>{example.earn} points</b> — worth <b>{inr(example.worth)}</b> on future orders. Points support decimals (e.g. 2.5).</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="save-loyalty" onClick={save} disabled={saving} className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg font-bold text-sm flex items-center gap-2"><Save size={16} /> {saving ? "Saving…" : "Save Settings"}</button>
          <button data-testid="send-nudges" onClick={sendNudges} className="px-5 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 rounded-lg font-bold text-sm flex items-center gap-2"><Send size={15} /> Send reward nudges now</button>
        </div>
      </div>
      <style>{`.inp{width:100%;border:1px solid #cbd5e1;border-radius:8px;padding:8px 12px;font-size:14px}.inp:focus{outline:2px solid #0B8E36;border-color:#0B8E36}`}</style>
    </div>
  );
}
const F = ({ label, children }) => <div><label className="text-xs font-semibold text-slate-500">{label}</label><div className="mt-1">{children}</div></div>;
const RoiCard = ({ label, value }) => <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p><p className="text-2xl font-extrabold text-slate-900 mt-1">{value}</p></div>;
